import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { Box, Group, Stack, Blockquote, Progress, Anchor } from '@mantine/core'
import { AppBadge, AppButton, AppModal, AppSelect, AppText, AppTextarea } from '../../app/common/ui'
import { Link } from 'react-router-dom'
import { useStore } from '../../app/stores/store'
import { EmptyState, AmmoScoreBadge } from '../../app/common/ui'
import { FlagTargetType, ResolveAction } from '../../app/models/flag'
import {
  VIOLATION_TYPE_OPTIONS,
  formatViolationPublicReason,
  getEscalationHint,
  getViolationPolicyPath,
  suggestViolationFromReason,
  FlagViolationType
} from '../../app/models/violationPolicy'
import { toast } from 'react-toastify'

const TARGET_TYPE_LABEL: Record<number, string> = {
  [FlagTargetType.Post]: 'Post',
  [FlagTargetType.Comment]: 'Comment',
  [FlagTargetType.Part]: 'Part',
  [FlagTargetType.FirearmModel]: 'Firearm model',
  [FlagTargetType.Build]: 'Build',
  [FlagTargetType.PostComment]: 'Post comment',
  [FlagTargetType.SlopPost]: 'Slop post'
}

const TARGET_TYPE_COLOR: Record<number, string> = {
  [FlagTargetType.Post]: 'blue',
  [FlagTargetType.Comment]: 'orange',
  [FlagTargetType.Part]: 'green',
  [FlagTargetType.FirearmModel]: 'navy',
  [FlagTargetType.Build]: 'gray',
  [FlagTargetType.PostComment]: 'grape',
  [FlagTargetType.SlopPost]: 'pink'
}

function getResolutionPlaceholder (action: string): string {
  if (action === ResolveAction.Dismiss) return 'Optional note...'
  if (action === ResolveAction.Warn) return 'Describe the warning reason...'
  if (action === ResolveAction.Escalate) return 'Why are you escalating this?'
  return 'Required resolution...'
}

interface ResolveModalProps {
  resolveModal: { action: string } | null;
  resolveModalTitle: string;
  resolveButtonColor: string;
  resolution: string;
  setResolution: (s: string) => void;
  violationType: number;
  setViolationType: (v: number) => void;
  subReason: string;
  setSubReason: (s: string) => void;
  setResolveModal: (v: { action: string } | null) => void;
  submitting: boolean;
  removeMessage: string;
  targetOwnerUsername: string;
  onConfirm: () => Promise<void>;
}

function ResolveModal ({
  resolveModal,
  resolveModalTitle,
  resolveButtonColor,
  resolution,
  setResolution,
  violationType,
  setViolationType,
  subReason,
  setSubReason,
  setResolveModal,
  submitting,
  removeMessage,
  targetOwnerUsername,
  onConfirm
}: ResolveModalProps) {
  const handleClose = () => {
    setResolveModal(null)
    setResolution('')
    setSubReason('')
  }
  if (!resolveModal) return null
  const requiresViolation = resolveModal.action !== ResolveAction.Dismiss
  const policyPath = getViolationPolicyPath(violationType)
  const previewReason = requiresViolation ? formatViolationPublicReason(violationType, subReason) : ''
  const escalationHint =
    resolveModal.action === ResolveAction.Escalate ? getEscalationHint(violationType) : null

  const message =
    resolveModal.action === ResolveAction.Dismiss
      ? 'Dismiss this flag with no action taken.'
      : resolveModal.action === ResolveAction.Warn
        ? `Issue a warning to @${targetOwnerUsername}. This will be logged and affect their AmmoScore.`
        : resolveModal.action === ResolveAction.Remove
          ? removeMessage
          : 'Escalate this flag to admin review.'

  return (
    <AppModal opened={true} title={resolveModalTitle} onClose={handleClose} size="md">
      <Stack>
        <AppText size="sm" color="dimmed">
          {message}
        </AppText>
        {requiresViolation && (
          <>
            <AppSelect
              label="Violation type"
              description="Maps to community guidelines or Terms of Service."
              data={VIOLATION_TYPE_OPTIONS}
              value={String(violationType)}
              onChange={(v) => setViolationType(v != null ? Number(v) : FlagViolationType.Other)}
              required
            />
            <AppTextarea
              label="Sub-reason (optional)"
              description="Short detail shown to the user alongside the violation category."
              placeholder="e.g. targeted insults in comments"
              value={subReason}
              onChange={(e) => setSubReason(e.currentTarget.value)}
              minRows={2}
            />
            <Box
              sx={{
                padding: 10,
                borderRadius: 8,
                border: '1px solid var(--gs-border)',
                background: 'var(--gs-surface2)'
              }}
            >
              <AppText size="xs" color="dimmed" mb={4}>
                User-facing reason preview
              </AppText>
              <AppText size="sm">{previewReason}</AppText>
              {policyPath && (
                <Anchor component={Link} to={policyPath} size="xs" mt={6} sx={{ display: 'inline-block' }}>
                  View policy →
                </Anchor>
              )}
            </Box>
            {escalationHint && (
              <AppText size="xs" color="dimmed" sx={{ fontStyle: 'italic' }}>
                {escalationHint}
              </AppText>
            )}
          </>
        )}
        <AppTextarea
          label="Moderator notes"
          placeholder={getResolutionPlaceholder(resolveModal.action)}
          value={resolution}
          onChange={(e) => setResolution(e.currentTarget.value)}
          minRows={3}
          required={resolveModal.action !== ResolveAction.Dismiss && resolveModal.action !== ResolveAction.Escalate}
        />
        <Group position="right">
          <AppButton variant="default" onClick={handleClose}>
            Cancel
          </AppButton>
          <AppButton
            color={resolveButtonColor}
            onClick={onConfirm}
            loading={submitting}
            disabled={
              (resolveModal.action !== ResolveAction.Dismiss &&
                resolveModal.action !== ResolveAction.Escalate &&
                !resolution.trim()) ||
              (requiresViolation && violationType == null)
            }
          >
            Confirm
          </AppButton>
        </Group>
      </Stack>
    </AppModal>
  )
}

export default observer(function FlagPreviewPane () {
  const { moderationStore } = useStore()
  const { selectedFlag, resolveFlag, loadFlags } = moderationStore
  const [resolveModal, setResolveModal] = useState<{ action: string } | null>(null)
  const [resolution, setResolution] = useState('')
  const [violationType, setViolationType] = useState<number>(FlagViolationType.Other)
  const [subReason, setSubReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleConfirmResolve = async () => {
    if (!selectedFlag || !resolveModal) return
    const action = resolveModal.action
    const requiresViolation = action !== ResolveAction.Dismiss
    if (requiresViolation && violationType == null) {
      toast.error('Select a violation type.')
      return
    }
    const notesRequired = action !== ResolveAction.Dismiss && action !== ResolveAction.Escalate
    if (notesRequired && !resolution.trim()) return
    setSubmitting(true)
    try {
      await resolveFlag(
        selectedFlag.id,
        action,
        resolution.trim(),
        requiresViolation ? violationType : undefined,
        requiresViolation ? subReason.trim() : undefined
      )
      moderationStore.selectFlag(null)
      loadFlags(moderationStore.filters)
      if (action === ResolveAction.Dismiss) toast.success('Flag dismissed')
      else if (action === ResolveAction.Warn) toast.warning('Warning issued')
      else if (action === ResolveAction.Remove) toast.error('Content removed / action logged')
      else if (action === ResolveAction.Escalate) toast.info('Flag escalated')
    } catch {
      // interceptor
    } finally {
      setSubmitting(false)
      setResolveModal(null)
      setResolution('')
      setSubReason('')
    }
  }

  const openResolve = (action: string) => {
    setResolveModal({ action })
    setResolution('')
    setSubReason('')
    if (action !== ResolveAction.Dismiss && selectedFlag) {
      setViolationType(suggestViolationFromReason(selectedFlag.reason))
    } else {
      setViolationType(FlagViolationType.Other)
    }
  }

  if (!selectedFlag) {
    return (
      <EmptyState
        title="Select a flag"
        message="Choose a flag from the list to review it here."
      />
    )
  }

  const f = selectedFlag
  const accuracy = f.reporterFlagsSubmitted > 0
    ? Math.round((f.reporterFlagsUpheld / f.reporterFlagsSubmitted) * 100)
    : 0
  const accuracyColor = accuracy > 70 ? 'green' : accuracy < 30 ? 'yellow' : 'blue'

  const removeMessage = f.targetCanBeHidden
    ? `Hide this content from non-moderators and log a moderation action against @${f.targetOwnerUsername}. This will affect their AmmoScore.`
    : `Log a moderation action against @${f.targetOwnerUsername}. This content type cannot be hidden but the action will be recorded.`

  const resolveModalTitle =
    resolveModal?.action === ResolveAction.Dismiss
      ? 'Dismiss flag'
      : resolveModal?.action === ResolveAction.Warn
        ? 'Warn user'
        : resolveModal?.action === ResolveAction.Remove
          ? 'Remove content'
          : 'Escalate'

  const resolveButtonColor =
    resolveModal?.action === ResolveAction.Dismiss
      ? 'gray'
      : resolveModal?.action === ResolveAction.Warn
        ? 'yellow'
        : resolveModal?.action === ResolveAction.Remove
          ? 'red'
          : 'violet'

  return (
    <Stack spacing="lg">
      {/* Section A — Flagged Content */}
      <Box>
        <Group spacing="xs" mb="xs">
          <AppBadge color={TARGET_TYPE_COLOR[f.targetType]} size="sm">
            {TARGET_TYPE_LABEL[f.targetType]}
          </AppBadge>
          <AppText weight={600} size="md">
            {f.targetTitle || '—'}
          </AppText>
        </Group>
        <Box
          sx={{
            padding: 12,
            borderRadius: 8,
            border: '1px solid var(--mantine-color-gray-3)',
            backgroundColor: 'var(--mantine-color-gray-0)'
          }}
        >
          <AppText size="xs" color="dimmed" mb={4}>
            @{f.targetOwnerUsername}
          </AppText>
          <Group spacing="xs">
            <AmmoScoreBadge tier={f.targetOwnerTier} score={f.targetOwnerAmmoScore ?? 0} size="sm" />
            <AppText size="xs" color="dimmed">
              {f.targetOwnerFlagsAgainst} previous flags on their content
            </AppText>
          </Group>
        </Box>
        <Blockquote mt="sm" cite={null} styles={{ cite: { display: 'none' } }}>
          <AppText size="sm">{f.reason}</AppText>
        </Blockquote>
        {f.groupCount > 1 && (
          <Box
            mt="sm"
            sx={{
              padding: 12,
              borderRadius: 8,
              backgroundColor: 'var(--mantine-color-yellow-0)',
              border: '1px solid var(--mantine-color-yellow-3)'
            }}
          >
            <AppText size="sm">
              This content has been reported {f.groupCount} times. Resolving this flag will not auto-resolve the others.
            </AppText>
          </Box>
        )}
        {!f.targetCanBeHidden && (
          <Box
            mt="sm"
            sx={{
              padding: 12,
              borderRadius: 8,
              backgroundColor: 'var(--mantine-color-gray-1)',
              border: '1px solid var(--mantine-color-gray-3)'
            }}
          >
            <AppText size="sm" color="dimmed">
              Note: this content type cannot be hidden. The Remove action will resolve the flag and log it but will not hide the content.
            </AppText>
          </Box>
        )}
      </Box>

      {/* Section B — Reporter Context */}
      <Box>
        <AppText size="sm" weight={600} mb="xs">
          Reporter context
        </AppText>
        <Box
          sx={{
            padding: 12,
            borderRadius: 8,
            border: '1px solid var(--mantine-color-gray-3)',
            backgroundColor: 'var(--mantine-color-gray-0)'
          }}
        >
          <AppText size="xs" color="dimmed">
            Reported by @{f.reporterUsername}
          </AppText>
          <Group spacing="xs" mt={4}>
            <AmmoScoreBadge tier={f.reporterTier} score={f.reporterAmmoScore ?? 0} size="sm" />
            <AppText size="xs">
              {f.reporterFlagsSubmitted} flags submitted · {f.reporterFlagsUpheld} upheld
            </AppText>
          </Group>
          <AppText size="xs" mt="xs">
            Flag accuracy rate: {accuracy}%
          </AppText>
          <Progress value={accuracy} color={accuracyColor} size="sm" mt={4} />
        </Box>
      </Box>

      {/* Section C — Action Buttons */}
      <Box>
        <AppText size="sm" weight={600} mb="xs">
          Actions
        </AppText>
        <Group spacing="sm">
          <AppButton variant="light" color="gray" onClick={() => openResolve(ResolveAction.Dismiss)}>
            Dismiss
          </AppButton>
          <AppButton variant="light" color="yellow" onClick={() => openResolve(ResolveAction.Warn)}>
            Warn User
          </AppButton>
          <AppButton
            variant="light"
            color="red"
            onClick={() => openResolve(ResolveAction.Remove)}
            title={!f.targetCanBeHidden ? 'Cannot hide this content type — flag will be logged only.' : undefined}
            sx={!f.targetCanBeHidden ? { textDecoration: 'line-through' } : undefined}
          >
            Remove
          </AppButton>
          <AppButton variant="light" color="violet" onClick={() => openResolve(ResolveAction.Escalate)}>
            Escalate
          </AppButton>
        </Group>
      </Box>

      {/* Resolve modal with textarea */}
      <ResolveModal
        resolveModal={resolveModal}
        resolveModalTitle={resolveModalTitle}
        resolveButtonColor={resolveButtonColor}
        resolution={resolution}
        setResolution={setResolution}
        violationType={violationType}
        setViolationType={setViolationType}
        subReason={subReason}
        setSubReason={setSubReason}
        setResolveModal={setResolveModal}
        submitting={submitting}
        removeMessage={removeMessage}
        targetOwnerUsername={f.targetOwnerUsername}
        onConfirm={handleConfirmResolve}
      />
    </Stack>
  )
})
